/**
 * 聊天应用主组件
 */
import { createAPIClient, type APIClient, type Message } from '../api/client.js';
import { createWSClient, type WSClient } from '../ws/client.js';
import { MessageList } from './MessageList.js';
import { InputBox, type SendKeyMode } from './InputBox.js';
import { StatusBar } from './StatusBar.js';
import type { WSHUDStatusMessage } from '../api/types.js';
import { adjustTextareaHeight, isNearBottom, scrollToBottom } from '../utils/ui.js';
import { storage } from '../utils/storage.js';

export interface ChatAppOptions {
  token: string;
  chatJid: string | null;
  apiBaseUrl: string;
  wsUrl: string;
  appName: string;
  onLogout: () => void;
  onShowSettings: () => void;
  onShowAgentMonitor?: () => void;
}

export class ChatApp {
  private options: ChatAppOptions;
  private apiClient: APIClient;
  private wsClient: WSClient;
  private messageList: MessageList;
  private inputBox: InputBox;
  private statusBar: StatusBar;
  private typingIndicator: HTMLElement | null = null;
  private scrollBtn: HTMLElement | null = null;
  private messagesContainer: HTMLElement | null = null;
  private taskStatusBar: HTMLElement | null = null;
  private isUserScrolling = false;
  private historyLoaded = false; // 防止重复加载历史消息

  constructor(options: ChatAppOptions) {
    this.options = options;
    this.apiClient = createAPIClient(options.apiBaseUrl);
    this.wsClient = createWSClient(options.wsUrl);

    // 设置 Token
    this.apiClient.setToken(options.token);
    this.wsClient.setToken(options.token);

    // 更新页面标题
    document.title = `${options.appName} - Web Chat`;

    // 初始化子组件
    this.messageList = new MessageList({
      onLoadHistory: (before?: string) => this.loadHistory(false, before),
    });

    this.inputBox = new InputBox({
      onSendMessage: (content) => this.handleSendMessage(content),
      onTypingChange: (isTyping) => this.handleTypingChange(isTyping),
      onFileUpload: (file) => this.handleFileUpload(file),
    });

    // 初始化 HUD 状态栏
    this.statusBar = new StatusBar();
  }

  /**
   * 初始化组件
   */
  private init(): void {
    // 获取 DOM 元素
    this.messagesContainer = document.getElementById('messages');
    this.typingIndicator = document.getElementById('typing-indicator');
    this.scrollBtn = document.getElementById('scroll-to-bottom');
    this.taskStatusBar = document.getElementById('task-status-bar');

    // 设置 WebSocket 事件监听
    this.wsClient.setListeners({
      onOpen: () => {
        console.log('WebSocket connected');
      },
      onAuthSuccess: () => {
        console.log('WebSocket authenticated');
        // 只在首次认证成功时加载历史消息，避免重复加载
        if (!this.historyLoaded) {
          this.loadHistory();
          this.historyLoaded = true;
        }
      },
      onAuthFailed: (error) => {
        console.error('WebSocket auth failed:', error);
        this.handleLogout();
      },
      onMessage: (sender, content, timestamp, senderName, model) => {
        this.messageList.addMessage(sender, content, timestamp, senderName, model);
        // 等待 DOM 更新后再滚动
        requestAnimationFrame(() => {
          this.scrollToBottomIfNear();
        });
      },
      onTyping: (isTyping) => {
        this.showTyping(isTyping);
      },
      onTaskStatus: (status, message, progress) => {
        this.handleTaskStatus(status, message, progress);
      },
      onHUDStatus: (hudStatus) => {
        this.handleHUDStatus(hudStatus);
      },
    });

    // 连接 WebSocket
    this.wsClient.connect();

    // 设置按钮事件
    this.setupButtonEvents();

    // 设置滚动监听
    this.setupScrollListener();

    // 设置滚动按钮事件
    this.scrollBtn?.addEventListener('click', () => {
      scrollToBottom(this.messagesContainer!);
      this.scrollBtn!.style.display = 'none';
    });
  }

  /**
   * 设置按钮事件
   */
  private setupButtonEvents(): void {
    const monitorBtn = document.getElementById('monitor-btn');
    const settingsBtn = document.getElementById('settings-btn');
    const logoutBtn = document.getElementById('logout-btn');

    monitorBtn?.addEventListener('click', () => {
      if (this.options.onShowAgentMonitor) {
        this.options.onShowAgentMonitor();
      }
    });

    settingsBtn?.addEventListener('click', () => {
      this.options.onShowSettings();
    });

    logoutBtn?.addEventListener('click', () => {
      this.handleLogout();
    });
  }

  /**
   * 设置滚动监听
   */
  private setupScrollListener(): void {
    if (!this.messagesContainer) return;

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    this.messagesContainer.addEventListener('scroll', () => {
      // 检测是否接近底部
      const nearBottom = isNearBottom(this.messagesContainer!);

      if (nearBottom) {
        this.isUserScrolling = false;
        this.scrollBtn!.style.display = 'none';
      } else {
        this.isUserScrolling = true;
        this.scrollBtn!.style.display = 'flex';
      }

      // 检测是否滚动到顶部，加载更多历史消息
      if (this.messagesContainer!.scrollTop === 0) {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.messageList.loadMore();
        }, 300);
      }
    });
  }

  /**
   * 加载历史消息
   * @param scrollOnLoad 是否加载后滚动到底部（首次加载时为 true）
   * @param before 可选的时间戳，用于分页加载
   */
  private async loadHistory(scrollOnLoad = true, before?: string): Promise<void> {
    try {
      // 默认加载最新的5条消息，滚动加载更多时使用 before 参数
      const limit = before ? 5 : 5;
      const response = before
        ? await this.apiClient.getMoreHistory(before, limit)
        : await this.apiClient.getHistory(limit);

      if (response.messages && response.messages.length > 0) {
        if (before) {
          // 加载更多：在现有消息前面添加
          this.messageList.prependMessages(response.messages);
        } else {
          // 首次加载：从 localStorage 获取旧的历史消息
          const storedHistory = this.getStoredHistory();

          // 合并服务器消息和本地存储消息，去重
          const allMessages = this.mergeMessages(storedHistory, response.messages);

          // 保存旧的服务器消息到 localStorage
          if (response.messages.length === limit) {
            this.saveToLocalStorage(response.messages);
          }

          // 设置消息，如果需要则滚动到底部
          this.messageList.setMessages(allMessages, scrollOnLoad);
        }
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }

  /**
   * 从 localStorage 获取历史消息
   */
  private getStoredHistory(): Message[] {
    try {
      const stored = localStorage.getItem('chat_history');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * 保存消息到 localStorage
   */
  private saveToLocalStorage(messages: Message[]): void {
    try {
      // 保存到 localStorage，用于下次加载时合并
      localStorage.setItem('chat_history', JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save to localStorage:', e);
    }
  }

  /**
   * 合并本地存储和服务器消息，去重
   */
  private mergeMessages(stored: Message[], server: Message[]): Message[] {
    const seen = new Set<string>();
    const merged: Message[] = [];

    // 先添加本地存储的消息（旧的）
    for (const msg of stored) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        merged.push(msg);
      }
    }

    // 再添加服务器的消息（新的），去重
    for (const msg of server) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        merged.push(msg);
      }
    }

    // 按时间排序
    merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return merged;
  }

  /**
   * 发送消息
   */
  private handleSendMessage(content: string): void {
    // 拦截 /add-* 和 /uninstall 命令
    const trimmed = content.trim();
    const installMatch = trimmed.match(/^\/(add-[\w-]+)$/);
    const uninstallMatch = trimmed.match(/^\/uninstall\s+([\w-]+)$/);

    if (installMatch) {
      const skillId = installMatch[1];
      this.messageList.addMessage('user', content, new Date().toISOString());
      requestAnimationFrame(() => this.scrollToBottomIfNear());
      this.handleSkillInstallFromChat(skillId);
      return;
    }

    if (uninstallMatch) {
      const skillId = uninstallMatch[1];
      this.messageList.addMessage('user', content, new Date().toISOString());
      requestAnimationFrame(() => this.scrollToBottomIfNear());
      this.handleSkillUninstallFromChat(skillId);
      return;
    }

    // 添加用户消息到界面（使用原始内容）
    this.messageList.addMessage('user', content, new Date().toISOString());

    // 🆕 检测是否需要配置提醒
    const configReminder = this.getConfigReminderIfNeeded(content);
    const finalContent = configReminder ? `${content}\n\n${configReminder}` : content;

    // 等待 DOM 更新后再滚动
    requestAnimationFrame(() => {
      this.scrollToBottomIfNear();
    });

    // 发送到 WebSocket（使用修改后的内容）
    this.wsClient.sendChat(finalContent);
  }

  /**
   * 通过聊天安装 Skill
   */
  private async handleSkillInstallFromChat(skillId: string): Promise<void> {
    this.messageList.addMessage('system', `正在安装 Skill "${skillId}"...`, new Date().toISOString());
    this.scrollToBottomIfNear();

    try {
      const result = await this.apiClient.installSkill(skillId);
      if (result.success) {
        const msg = result.requiresServiceRestart
          ? `Skill "${skillId}" v${result.version} 安装成功！需要重启服务才能生效。`
          : `Skill "${skillId}" v${result.version} 安装成功！`;
        this.messageList.addMessage('system', msg, new Date().toISOString());

        if (result.requiresServiceRestart) {
          if (confirm('Skill 已安装，需要重启服务。现在重启？')) {
            await this.apiClient.restartService();
            this.messageList.addMessage('system', '服务正在重启...', new Date().toISOString());
            setTimeout(() => location.reload(), 5000);
          }
        }
      } else {
        this.messageList.addMessage('system', `安装失败: ${result.error}`, new Date().toISOString());
      }
    } catch (err) {
      this.messageList.addMessage('system', `安装失败: ${err instanceof Error ? err.message : String(err)}`, new Date().toISOString());
    }
    this.scrollToBottomIfNear();
  }

  /**
   * 通过聊天卸载 Skill
   */
  private async handleSkillUninstallFromChat(skillId: string): Promise<void> {
    if (!confirm(`确定要卸载 Skill "${skillId}" 吗？`)) {
      this.messageList.addMessage('system', '已取消卸载', new Date().toISOString());
      return;
    }

    this.messageList.addMessage('system', `正在卸载 Skill "${skillId}"...`, new Date().toISOString());
    this.scrollToBottomIfNear();

    try {
      const result = await this.apiClient.uninstallSkill(skillId);
      if (result.success) {
        const msg = result.requiresServiceRestart
          ? `Skill "${skillId}" 卸载成功！需要重启服务才能生效。`
          : `Skill "${skillId}" 卸载成功！`;
        this.messageList.addMessage('system', msg, new Date().toISOString());

        if (result.requiresServiceRestart) {
          if (confirm('Skill 已卸载，需要重启服务。现在重启？')) {
            await this.apiClient.restartService();
            this.messageList.addMessage('system', '服务正在重启...', new Date().toISOString());
            setTimeout(() => location.reload(), 5000);
          }
        }
      } else {
        this.messageList.addMessage('system', `卸载失败: ${result.error}`, new Date().toISOString());
      }
    } catch (err) {
      this.messageList.addMessage('system', `卸载失败: ${err instanceof Error ? err.message : String(err)}`, new Date().toISOString());
    }
    this.scrollToBottomIfNear();
  }

  /**
   * 🆕 根据内容判断是否需要配置提醒
   */
  private getConfigReminderIfNeeded(content: string): string | null {
    const lowerContent = content.toLowerCase();

    // SP-API 禁止检测
    const spApiKeywords = ['sp-api', 'selling partner', '亚马逊 api', 'amazon api', 'spapi', 'selling-partner'];
    if (spApiKeywords.some(k => lowerContent.includes(k))) {
      return '⚠️ 系统提醒：禁止使用 SP-API（Selling Partner API）方案。用户明确禁止此方案，请使用其他方式（如网页抓取、手动上传等）。';
    }

    // 邮件配置检测
    const emailKeywords = ['发送报告', 'send email', '邮件发送', 'email report', 'smtp', '授权码'];
    if (emailKeywords.some(k => lowerContent.includes(k))) {
      return '📧 系统提醒：报告发送至 buysz@qq.com，使用 ripsy@qq.com 作为发送账号。推荐使用 resend 服务。';
    }

    // 任务创建检测
    const taskKeywords = ['创建任务', '新建任务', 'create task', 'new task', '安排任务', 'schedule task'];
    if (taskKeywords.some(k => lowerContent.includes(k))) {
      return '📋 系统提醒：任务创建前必须确认：1) 目标清晰明确 2) 有可量化的成功标准 3) 了解所需工具和技能 4) 确认资源充足。';
    }

    // 直接执行检测
    const directExecKeywords = ['直接执行', 'immediate execute', '跳过验证', 'skip verification'];
    if (directExecKeywords.some(k => lowerContent.includes(k))) {
      return '🚫 系统提醒：任务确定后必须先手动逐步执行，确保整个任务链路能完全跑通，然后才能设置自动化。';
    }

    return null;
  }

  /**
   * 处理打字状态变化
   */
  private handleTypingChange(isTyping: boolean): void {
    this.wsClient.sendTyping(isTyping);
  }

  /**
   * 处理文件上传
   * 返回图片 URL，由 InputBox 管理发送时机
   */
  private async handleFileUpload(file: File): Promise<string> {
    // 显示上传中状态
    this.inputBox.setUploadState('uploading');

    try {
      const response = await this.apiClient.uploadFile(file);

      if (response.success) {
        // 返回图片 URL，由 InputBox 决定何时发送
        return response.file.url;
      } else {
        throw new Error('上传失败');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const message = error instanceof Error ? error.message : '上传失败，请重试';
      throw new Error(message);
    } finally {
      // 状态由 InputBox 管理
      this.inputBox.setUploadState('idle');
    }
  }

  /**
   * 显示/隐藏打字指示器
   */
  private showTyping(show: boolean): void {
    if (this.typingIndicator) {
      this.typingIndicator.style.display = show ? 'flex' : 'none';
      // 不再自动滚动，让用户自己控制浏览位置
    }
  }

  /**
   * 处理任务状态更新 - 显示在消息状态区域
   */
  private handleTaskStatus(status: string, message?: string, progress?: number): void {
    // 更新消息列表中的状态指示器（现在显示在 message-status 区域）
    this.messageList.setMessageStatus('latest', status as 'running' | 'success' | 'error', message);

    // 隐藏消息下方的进度条（已移到 message-status）
    if (this.taskStatusBar) {
      this.taskStatusBar.style.display = 'none';
    }
  }

  /**
   * 处理 HUD 状态更新
   */
  private handleHUDStatus(hudStatus: WSHUDStatusMessage): void {
    this.statusBar.update(hudStatus);
  }

  /**
   * 如果用户没有手动滚动，则滚动到底部
   * 等待 DOM 更新和图片加载完成
   */
  private scrollToBottomIfNear(): void {
    if (!this.messagesContainer) return;

    // 检查是否接近底部（距离底部 150px 以内）
    const nearBottom = isNearBottom(this.messagesContainer, 150);
    if (!nearBottom) {
      // 用户在查看历史消息，不自动滚动
      return;
    }

    const doScroll = () => {
      if (this.messagesContainer && !this.isUserScrolling) {
        scrollToBottom(this.messagesContainer);
      }
    };

    // 使用双重 requestAnimationFrame 确保 DOM 完全渲染
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // 检查是否有新图片需要加载
        const images = this.messagesContainer!.querySelectorAll('img:not([data-loaded])');
        if (images.length > 0) {
          // 有图片，等待图片加载
          let loadedCount = 0;
          const totalImages = images.length;

          images.forEach((img) => {
            if (img.complete) {
              loadedCount++;
            } else {
              img.addEventListener('load', () => {
                loadedCount++;
                if (loadedCount === totalImages) {
                  doScroll();
                }
              });
              img.addEventListener('error', () => {
                loadedCount++;
                if (loadedCount === totalImages) {
                  doScroll();
                }
              });
            }
          });

          // 如果所有图片已经加载完成，立即滚动
          if (loadedCount === totalImages) {
            doScroll();
          }
        } else {
          // 没有新图片，直接滚动
          doScroll();
        }
      });
    });
  }

  /**
   * 处理登出
   */
  private handleLogout(): void {
    // 断开 WebSocket
    this.wsClient.disconnect();

    // 调用登出 API
    this.apiClient.logout().catch(console.error);

    // 清除存储
    storage.clearToken();
    storage.clearChatJid();

    // 触发登出回调
    this.options.onLogout();
  }

  /**
   * 显示聊天应用
   */
  show(): void {
    this.init();

    const loginScreen = document.getElementById('login-screen');
    const chatScreen = document.getElementById('chat-screen');

    if (loginScreen) loginScreen.style.display = 'none';
    if (chatScreen) chatScreen.style.display = 'flex';

    // 更新聊天界面左上角的应用名称
    const headerTitle = chatScreen?.querySelector('.header-left h1');
    if (headerTitle) {
      headerTitle.textContent = this.options.appName;
    }

    // 显示并初始化输入框
    this.inputBox.show();
  }

  /**
   * 获取 InputBox 实例（供 SettingsPanel 使用）
   */
  getInputBox(): InputBox {
    return this.inputBox;
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    // 断开 WebSocket
    this.wsClient.disconnect();

    // 销毁子组件
    this.messageList.destroy();
    this.inputBox.destroy();
  }
}
